import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  ArrowLeft, 
  Shield, 
  Plus, 
  Trash2, 
  Edit, 
  AlertTriangle, 
  Info, 
  AlertCircle,
  Check,
  X,
  Clock,
  Send,
  Users,
  UserCog,
  Crown,
  Activity,
  RefreshCw,
  DollarSign
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import SkimCard from './dashboard/SkimCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_V2 = `${BACKEND_URL}/api/v2`;

const AdminPanel = () => {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [botStopLogs, setBotStopLogs] = useState([]);
  const [liveData, setLiveData] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('messages');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  // v3.9.20: Skim moved from dashboard → admin-only
  const [skimStats, setSkimStats] = useState({ session_profit: 0, session_skim: 0, next_skim_at: 500, skim_trigger_amount: 500, skim_transfer_amount: 5, lifetime_skim: 0, total_skims: 0 });
  const [skimHistory, setSkimHistory] = useState([]);
  const [skimLoading, setSkimLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info',
    is_active: true,
    start_time: '',
    end_time: ''
  });

  const checkAdminStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_V2}/system/admin/check`, { withCredentials: true });
      setIsAdmin(response.data.is_admin);
      if (!response.data.is_admin) {
        toast.error('Access Denied', { description: 'Admin privileges required' });
        setTimeout(() => navigate('/dashboard'), 2000);
      }
    } catch (error) {
      console.error('Admin check failed:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchMessages = useCallback(async () => {
    try {
      const response = await axios.get(`${API_V2}/system/messages/all`, { withCredentials: true });
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      toast.error('Failed to load messages');
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_V2}/system/users`, { withCredentials: true });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    }
  }, []);

  const fetchBotStopLogs = useCallback(async () => {
    try {
      const response = await axios.get(`${API_V2}/system/bot-stop-logs?limit=200`, { withCredentials: true });
      setBotStopLogs(response.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch bot stop logs:', error);
      toast.error('Failed to load bot stop logs');
    }
  }, []);

  const clearBotStopLogs = async () => {
    if (!window.confirm('Are you sure you want to clear all bot stop logs?')) return;
    try {
      await axios.delete(`${API_V2}/system/bot-stop-logs`, { withCredentials: true });
      toast.success('Bot stop logs cleared');
      setBotStopLogs([]);
    } catch (error) {
      toast.error('Failed to clear logs');
    }
  };

  useEffect(() => {
    checkAdminStatus();
  }, [checkAdminStatus]);

  useEffect(() => {
    if (isAdmin) {
      fetchMessages();
      fetchUsers();
      fetchBotStopLogs();
    }
  }, [isAdmin, fetchMessages, fetchUsers, fetchBotStopLogs]);

  const handleRoleChange = async (userId, newLevel) => {
    try {
      await axios.post(`${API_V2}/monetization/set-level/${userId}`, { access_level: newLevel }, { withCredentials: true });
      toast.success(`User access level updated to ${newLevel}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update access level');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      message: '',
      type: 'info',
      is_active: true,
      start_time: '',
      end_time: ''
    });
    setShowCreateForm(false);
    setEditingMessage(null);
  };

  const fetchLiveDashboard = useCallback(async () => {
    setLiveLoading(true);
    try {
      const response = await axios.get(`${API_V2}/user/stats/admin/live-dashboard`, { withCredentials: true });
      setLiveData(response.data);
    } catch (error) {
      console.error('Failed to fetch live dashboard:', error);
      toast.error('Failed to load live dashboard');
    } finally {
      setLiveLoading(false);
    }
  }, []);

  // v3.9.20: Skim tab data (admin-only view of the current admin's own skim)
  const fetchSkimData = useCallback(async () => {
    setSkimLoading(true);
    try {
      const [statsRes, historyRes] = await Promise.all([
        axios.get(`${API_V2}/skim/stats`, { withCredentials: true }),
        axios.get(`${API_V2}/skim/history?limit=50`, { withCredentials: true }).catch(() => ({ data: { history: [] } })),
      ]);
      setSkimStats(statsRes.data || {});
      setSkimHistory(historyRes.data?.history || []);
    } catch (error) {
      console.error('Failed to fetch skim data:', error);
      toast.error('Failed to load skim data');
    } finally {
      setSkimLoading(false);
    }
  }, []);

  // Auto-refresh skim when tab is active
  useEffect(() => {
    if (activeTab === 'skim' && isAdmin) {
      fetchSkimData();
      const interval = setInterval(fetchSkimData, 15000);
      return () => clearInterval(interval);
    }
  }, [activeTab, isAdmin, fetchSkimData]);

  // Auto-refresh live dashboard when tab is active
  useEffect(() => {
    if (activeTab === 'live-dashboard' && isAdmin) {
      fetchLiveDashboard();
      const interval = setInterval(fetchLiveDashboard, 5000); // Refresh every 5 seconds
      return () => clearInterval(interval);
    }
  }, [activeTab, isAdmin, fetchLiveDashboard]);

  const handleCreate = async () => {
    if (!formData.title.trim() || !formData.message.trim()) {
      toast.error('Title and message are required');
      return;
    }

    try {
      const payload = {
        ...formData,
        start_time: formData.start_time || null,
        end_time: formData.end_time || null
      };
      
      await axios.post(`${API_V2}/system/messages`, payload, { withCredentials: true });
      toast.success('System message created');
      resetForm();
      fetchMessages();
    } catch (error) {
      toast.error('Failed to create message', { description: error.response?.data?.detail });
    }
  };

  const handleUpdate = async () => {
    if (!editingMessage) return;

    try {
      const payload = {
        ...formData,
        start_time: formData.start_time || null,
        end_time: formData.end_time || null
      };
      
      await axios.put(`${API_V2}/system/messages/${editingMessage.id}`, payload, { withCredentials: true });
      toast.success('System message updated');
      resetForm();
      fetchMessages();
    } catch (error) {
      toast.error('Failed to update message', { description: error.response?.data?.detail });
    }
  };

  const handleDelete = async (msgId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;

    try {
      await axios.delete(`${API_V2}/system/messages/${msgId}`, { withCredentials: true });
      toast.success('System message deleted');
      fetchMessages();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete message', { description: error.response?.data?.detail });
    }
  };

  const handleToggleActive = async (msg) => {
    try {
      await axios.put(`${API_V2}/system/messages/${msg.id}`, {
        is_active: !msg.is_active
      }, { withCredentials: true });
      toast.success(`Message ${msg.is_active ? 'deactivated' : 'activated'}`);
      fetchMessages();
    } catch (error) {
      toast.error('Failed to update message');
    }
  };

  const startEdit = (msg) => {
    setFormData({
      title: msg.title,
      message: msg.message,
      type: msg.type,
      is_active: msg.is_active,
      start_time: msg.start_time ? msg.start_time.slice(0, 16) : '',
      end_time: msg.end_time ? msg.end_time.slice(0, 16) : ''
    });
    setEditingMessage(msg);
    setShowCreateForm(true);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'warning': return <AlertTriangle className="h-4 w-4 text-yellow-400" />;
      case 'critical': return <AlertCircle className="h-4 w-4 text-red-400" />;
      default: return <Info className="h-4 w-4 text-blue-400" />;
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'warning': return <Badge className="bg-yellow-900 text-yellow-200 border-yellow-600">WARNING</Badge>;
      case 'critical': return <Badge className="bg-red-900 text-red-200 border-red-600">CRITICAL</Badge>;
      default: return <Badge className="bg-blue-900 text-blue-200 border-blue-600">INFO</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-[#00FF41] animate-pulse">Verifying admin access...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-red-500">Access Denied - Redirecting...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black p-4">
      <Toaster position="top-right" theme="dark" />
      
      {/* Header */}
      <header className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              className="matrix-btn rounded-none text-xs h-8"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              BACK
            </Button>
            <div className="flex items-center gap-2">
              <Shield className="h-6 w-6 text-[#00FF41]" />
              <h1 className="text-2xl font-bold text-[#00FF41] tracking-widest">
                ADMIN PANEL
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-3xl grid-cols-5 mb-6 bg-[#001500] border border-[#003B00]">
          <TabsTrigger 
            value="messages" 
            className="data-[state=active]:bg-[#003B00] data-[state=active]:text-[#00FF41] rounded-none"
          >
            <AlertTriangle className="h-4 w-4 mr-2" />
            MESSAGES
          </TabsTrigger>
          <TabsTrigger 
            value="users" 
            className="data-[state=active]:bg-[#003B00] data-[state=active]:text-[#00FF41] rounded-none"
          >
            <Users className="h-4 w-4 mr-2" />
            USERS
          </TabsTrigger>
          <TabsTrigger 
            value="bot-logs" 
            className="data-[state=active]:bg-[#003B00] data-[state=active]:text-[#00FF41] rounded-none"
            data-testid="bot-logs-tab"
          >
            <Activity className="h-4 w-4 mr-2" />
            BOT LOGS ({botStopLogs.length})
          </TabsTrigger>
          <TabsTrigger 
            value="skim" 
            className="data-[state=active]:bg-[#003B00] data-[state=active]:text-[#00FF41] rounded-none"
            data-testid="skim-tab"
          >
            <DollarSign className="h-4 w-4 mr-2" />
            SKIM
          </TabsTrigger>
          <TabsTrigger 
            value="live-dashboard" 
            className="data-[state=active]:bg-[#003B00] data-[state=active]:text-[#00FF41] rounded-none"
            data-testid="live-dashboard-tab"
          >
            <Crown className="h-4 w-4 mr-2" />
            LIVE TRADING
          </TabsTrigger>
        </TabsList>

        {/* Messages Tab */}
        <TabsContent value="messages">
          <div className="flex justify-end mb-4">
            <Button
              onClick={() => { resetForm(); setShowCreateForm(true); }}
              className="matrix-btn rounded-none"
              data-testid="create-message-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              NEW MESSAGE
            </Button>
          </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <Card className="matrix-card rounded-none border border-[#003B00] mb-6">
          <CardHeader className="border-b border-[#003B00] pb-3">
            <CardTitle className="text-[#00FF41] text-sm tracking-widest flex items-center gap-2">
              <Send className="h-4 w-4" />
              {editingMessage ? 'EDIT MESSAGE' : 'CREATE NEW MESSAGE'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[#008F11] text-xs mb-1 block">TITLE *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="System Maintenance"
                  className="bg-black border-[#003B00] text-[#00FF41]"
                  data-testid="message-title-input"
                />
              </div>
              <div>
                <label className="text-[#008F11] text-xs mb-1 block">TYPE *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full h-10 px-3 bg-black border border-[#003B00] text-[#00FF41] rounded-md"
                  data-testid="message-type-select"
                >
                  <option value="info">Info (Blue Banner)</option>
                  <option value="warning">Warning (Yellow Banner)</option>
                  <option value="critical">Critical (Red Modal Popup)</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="text-[#008F11] text-xs mb-1 block">MESSAGE *</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Scheduled maintenance window: The trading bot will be unavailable..."
                className="w-full h-24 px-3 py-2 bg-black border border-[#003B00] text-[#00FF41] rounded-md resize-none"
                data-testid="message-content-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[#008F11] text-xs mb-1 block">START TIME (Optional)</label>
                <Input
                  type="datetime-local"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="bg-black border-[#003B00] text-[#00FF41]"
                  data-testid="message-start-time"
                />
              </div>
              <div>
                <label className="text-[#008F11] text-xs mb-1 block">END TIME (Optional)</label>
                <Input
                  type="datetime-local"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  className="bg-black border-[#003B00] text-[#00FF41]"
                  data-testid="message-end-time"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4"
                id="is_active"
              />
              <label htmlFor="is_active" className="text-[#00FF41] text-sm">Active (show to users)</label>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={resetForm}
                className="border-[#003B00] text-[#008F11] hover:bg-[#001500]"
              >
                CANCEL
              </Button>
              <Button
                onClick={editingMessage ? handleUpdate : handleCreate}
                className="matrix-btn"
                data-testid="save-message-btn"
              >
                {editingMessage ? 'UPDATE' : 'CREATE'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Messages List */}
      <Card className="matrix-card rounded-none border border-[#003B00]">
        <CardHeader className="border-b border-[#003B00] pb-3">
          <CardTitle className="text-[#00FF41] text-sm tracking-widest flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            SYSTEM MESSAGES ({messages.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {messages.length === 0 ? (
            <div className="p-8 text-center text-[#008F11]">
              No system messages. Click NEW MESSAGE to create one.
            </div>
          ) : (
            <ScrollArea className="h-[calc(100vh-400px)]">
              <div className="divide-y divide-[#003B00]">
                {messages.map((msg) => (
                  <div 
                    key={msg.id} 
                    className={`p-4 ${msg.is_active ? 'bg-[#001500]/30' : 'opacity-50'}`}
                    data-testid={`message-row-${msg.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        {getTypeIcon(msg.type)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[#00FF41] font-semibold">{msg.title}</span>
                            {getTypeBadge(msg.type)}
                            {msg.is_active ? (
                              <Badge className="bg-green-900 text-green-200 border-green-600">ACTIVE</Badge>
                            ) : (
                              <Badge className="bg-gray-800 text-gray-400 border-gray-600">INACTIVE</Badge>
                            )}
                          </div>
                          <p className="text-[#008F11] text-sm">{msg.message}</p>
                          {(msg.start_time || msg.end_time) && (
                            <div className="flex items-center gap-4 mt-2 text-xs text-[#006600]">
                              <Clock className="h-3 w-3" />
                              {msg.start_time && <span>From: {new Date(msg.start_time).toLocaleString()}</span>}
                              {msg.end_time && <span>Until: {new Date(msg.end_time).toLocaleString()}</span>}
                            </div>
                          )}
                          <p className="text-[#004400] text-xs mt-1">
                            Created by {msg.created_by_name} on {new Date(msg.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleToggleActive(msg)}
                          className="h-8 w-8 p-0 text-[#00FF41] hover:bg-[#003B00]"
                          title={msg.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {msg.is_active ? <X className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => startEdit(msg)}
                          className="h-8 w-8 p-0 text-[#00FF41] hover:bg-[#003B00]"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('Delete clicked for:', msg.id);
                            handleDelete(msg.id);
                          }}
                          className="h-8 w-8 p-0 text-red-500 hover:bg-red-900/30"
                          title="Delete"
                          data-testid={`delete-message-${msg.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card className="matrix-card rounded-none border border-[#003B00]">
            <CardHeader className="border-b border-[#003B00] pb-3">
              <CardTitle className="text-[#00FF41] text-sm tracking-widest flex items-center gap-2">
                <UserCog className="h-4 w-4" />
                USER MANAGEMENT ({users.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {users.length === 0 ? (
                <div className="p-8 text-center text-[#008F11]">
                  No users found.
                </div>
              ) : (
                <ScrollArea className="h-[calc(100vh-300px)]">
                  <div className="divide-y divide-[#003B00]">
                    {users.map((user) => (
                      <div 
                        key={user.user_id} 
                        className="p-4 flex items-center justify-between gap-4"
                        data-testid={`user-row-${user.user_id}`}
                      >
                        <div className="flex items-center gap-3">
                          {user.picture ? (
                            <img 
                              src={user.picture} 
                              alt={user.name} 
                              className="h-10 w-10 rounded-full border border-[#003B00]"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full border border-[#003B00] bg-[#001500] flex items-center justify-center">
                              <Users className="h-5 w-5 text-[#008F11]" />
                            </div>
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-[#00FF41] font-semibold cursor-pointer hover:underline" onClick={() => navigate(`/profile/${user.user_id}`)} data-testid={`admin-user-profile-${user.user_id}`}>{user.name || 'Unknown'}</span>
                              {(user.access_level === 'GOD') && (
                                <Crown className="h-4 w-4 text-yellow-500" />
                              )}
                            </div>
                            <p className="text-[#008F11] text-xs">{user.email}</p>
                            <p className="text-[#006600] text-xs">ID: {user.user_id}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge 
                            className={
                              (user.access_level === 'GOD') ? 'bg-red-900 text-red-200 border-red-600' :
                              (user.access_level === 'FAMILY') ? 'bg-purple-900 text-purple-200 border-purple-600' :
                              (user.access_level === 'PAID') ? 'bg-blue-900 text-blue-200 border-blue-600' :
                              'bg-gray-900 text-gray-200 border-gray-600'
                            }
                          >
                            {user.access_level || 'FREE'}
                          </Badge>
                          <select
                            value={user.access_level || 'FREE'}
                            onChange={(e) => handleRoleChange(user.user_id, e.target.value)}
                            className="h-8 px-2 bg-black border border-[#003B00] text-[#00FF41] rounded text-xs"
                            data-testid={`role-select-${user.user_id}`}
                          >
                            <option value="FREE">Free</option>
                            <option value="PAID">Paid</option>
                            <option value="FAMILY">Family</option>
                            <option value="GOD">GOD</option>
                          </select>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bot Stop Logs Tab */}
        <TabsContent value="bot-logs">
          <Card className="matrix-card rounded-none border border-[#003B00]">
            <CardHeader className="border-b border-[#003B00] pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-[#00FF41] text-sm tracking-widest flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  BOT STOP LOGS ({botStopLogs.length})
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={fetchBotStopLogs}
                    className="matrix-btn rounded-none text-xs h-8"
                    data-testid="refresh-logs-btn"
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    REFRESH
                  </Button>
                  <Button
                    onClick={clearBotStopLogs}
                    className="matrix-btn rounded-none text-xs h-8 border-red-500 text-red-500 hover:bg-red-900/30"
                    data-testid="clear-logs-btn"
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    CLEAR ALL
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {botStopLogs.length === 0 ? (
                <div className="p-8 text-center text-[#008F11]">
                  No bot stop events logged yet. When bots stop unexpectedly, the reason will appear here.
                </div>
              ) : (
                <ScrollArea className="h-[calc(100vh-300px)]">
                  <div className="divide-y divide-[#003B00]">
                    {botStopLogs.map((log, index) => (
                      <div key={index} className="p-4 hover:bg-[#001500]">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[#00FF41] font-bold text-sm">
                                {log.user_name || 'Unknown User'}
                              </span>
                              {log.error_type && (
                                <Badge className="bg-red-900 text-red-200 border-red-600 text-xs">
                                  {log.error_type}
                                </Badge>
                              )}
                              <Badge className={`text-xs ${
                                log.trading_mode === 'live' 
                                  ? 'bg-red-900 text-red-200 border-red-600' 
                                  : 'bg-blue-900 text-blue-200 border-blue-600'
                              }`}>
                                {log.trading_mode?.toUpperCase() || 'UNKNOWN'}
                              </Badge>
                            </div>
                            <p className="text-yellow-400 text-sm font-mono mb-1">
                              {log.reason}
                            </p>
                            {log.error_details && (
                              <p className="text-red-400 text-xs font-mono bg-red-900/20 p-2 rounded mt-1 break-all">
                                {log.error_details}
                              </p>
                            )}
                            <div className="flex items-center gap-4 text-xs text-[#008F11] mt-2">
                              <span>Iterations: {log.iteration_count || 0}</span>
                              <span>{log.user_email}</span>
                            </div>
                          </div>
                          <div className="text-right text-xs text-[#006400] whitespace-nowrap">
                            {log.timestamp ? new Date(log.timestamp).toLocaleString() : 'Unknown time'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Skim Tab (v3.9.20: moved from dashboard) */}
        <TabsContent value="skim">
          <Card className="bg-[#0a0a0a] border-[#003B00]" data-testid="skim-tab-content">
            <CardHeader className="border-b border-[#003B00]">
              <div className="flex items-center justify-between">
                <CardTitle className="text-[#00FF41] font-mono flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-yellow-500" />
                  THE SKIM
                </CardTitle>
                <Button
                  onClick={fetchSkimData}
                  variant="outline"
                  size="sm"
                  className="border-[#003B00] hover:border-[#00FF41] text-[#00FF41]"
                  data-testid="skim-refresh-btn"
                >
                  <RefreshCw className={`h-4 w-4 ${skimLoading ? 'animate-spin' : ''}`} strokeWidth={1.5} />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <SkimCard skimData={skimStats} lifetimeSkim={skimStats?.lifetime_skim || 0} />

              {/* History */}
              <div className="border border-[#003B00] rounded-none">
                <div className="border-b border-[#003B00] p-3 flex items-center justify-between">
                  <span className="text-xs uppercase tracking-widest text-[#00FF41]">
                    RECENT TRANSFERS ({skimHistory.length})
                  </span>
                  <span className="text-[10px] text-[#008F11]">
                    Total skims: {skimStats?.total_skims || 0}
                  </span>
                </div>
                {skimHistory.length === 0 ? (
                  <div className="p-6 text-xs text-[#008F11] text-center" data-testid="skim-history-empty">
                    No skim transfers yet. When realized profit hits ${skimStats?.skim_trigger_amount || 500},
                    ${skimStats?.skim_transfer_amount || 5} will move to your skim wallet.
                  </div>
                ) : (
                  <ul className="divide-y divide-[#003B00] text-xs" data-testid="skim-history-list">
                    {skimHistory.slice(0, 20).map((h, idx) => (
                      <li key={idx} className="flex items-center justify-between px-3 py-2 text-[#00FF41]">
                        <span className="opacity-70">
                          {h.timestamp ? new Date(h.timestamp).toLocaleString() : '—'}
                        </span>
                        <span className="opacity-60">
                          Milestone #{h.milestone_num ?? '—'}
                        </span>
                        <span className="text-yellow-500">
                          ${Number(h.transfer_amount ?? h.amount ?? 0).toFixed(2)}
                        </span>
                        <Badge variant="outline" className="text-[9px] border-[#003B00]">
                          {(h.mode || '').toUpperCase() || 'SIM'}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Live Trading Dashboard Tab */}
        <TabsContent value="live-dashboard">
          <Card className="bg-[#0a0a0a] border-[#003B00]">
            <CardHeader className="border-b border-[#003B00]">
              <div className="flex items-center justify-between">
                <CardTitle className="text-[#00FF41] font-mono flex items-center gap-2">
                  <Crown className="h-5 w-5 text-yellow-500" />
                  LIVE TRADING DASHBOARD
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchLiveDashboard}
                  disabled={liveLoading}
                  className="border-[#003B00] text-[#00FF41] hover:bg-[#003B00]"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${liveLoading ? 'animate-spin' : ''}`} />
                  REFRESH
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {liveLoading && !liveData ? (
                <div className="text-center text-[#008F11] py-10">Loading live data...</div>
              ) : !liveData?.success ? (
                <div className="text-center text-red-500 py-10">Failed to load live data</div>
              ) : (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-[#001500] border border-[#003B00] p-4 rounded">
                      <div className="text-[#008F11] text-xs mb-1">LIVE USERS</div>
                      <div className="text-[#00FF41] text-2xl font-mono">{liveData.summary?.total_live_users || 0}</div>
                    </div>
                    <div className="bg-[#001500] border border-[#003B00] p-4 rounded">
                      <div className="text-[#008F11] text-xs mb-1">TOTAL BUYS</div>
                      <div className="text-cyan-400 text-2xl font-mono">{liveData.summary?.total_live_buys || 0}</div>
                    </div>
                    <div className="bg-[#001500] border border-[#003B00] p-4 rounded">
                      <div className="text-[#008F11] text-xs mb-1">TOTAL SELLS</div>
                      <div className="text-orange-400 text-2xl font-mono">{liveData.summary?.total_live_sells || 0}</div>
                    </div>
                    <div className="bg-[#001500] border border-[#003B00] p-4 rounded">
                      <div className="text-[#008F11] text-xs mb-1">TOTAL PROFIT</div>
                      <div className={`text-2xl font-mono ${(liveData.summary?.total_live_profit || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        ${(liveData.summary?.total_live_profit || 0).toFixed(2)}
                      </div>
                    </div>
                    <div className="bg-[#001500] border border-[#003B00] p-4 rounded">
                      <div className="text-[#008F11] text-xs mb-1">TOTAL VOLUME</div>
                      <div className="text-yellow-400 text-2xl font-mono">${(liveData.summary?.total_live_volume || 0).toFixed(2)}</div>
                    </div>
                  </div>

                  {/* Profit by Pair */}
                  {liveData.profit_by_pair && Object.keys(liveData.profit_by_pair).length > 0 && (
                    <div className="bg-[#001500] border border-[#003B00] p-4 rounded">
                      <h3 className="text-[#00FF41] font-mono mb-3">PROFIT BY PAIR</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {Object.entries(liveData.profit_by_pair).map(([pair, data]) => (
                          <div key={pair} className="bg-[#0a0a0a] border border-[#003B00] p-3 rounded">
                            <div className="text-cyan-400 text-sm font-mono">{pair}</div>
                            <div className={`text-lg font-mono ${data.profit >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              ${data.profit.toFixed(2)}
                            </div>
                            <div className="text-[#008F11] text-xs">{data.trades} trades</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Per-User Stats */}
                  <div className="bg-[#001500] border border-[#003B00] p-4 rounded">
                    <h3 className="text-[#00FF41] font-mono mb-3">LIVE TRADERS</h3>
                    {liveData.users?.length === 0 ? (
                      <div className="text-[#008F11] text-center py-4">No users in LIVE mode</div>
                    ) : (
                      <div className="space-y-4">
                        {liveData.users?.map((u) => (
                          <div key={u.user_id} className="bg-[#0a0a0a] border border-[#003B00] p-4 rounded">
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <span className="text-[#00FF41] font-mono">{u.name || 'Unknown'}</span>
                                <span className="text-[#008F11] text-sm ml-2">({u.email})</span>
                              </div>
                              <Badge 
                                variant="outline" 
                                className={u.status === 'running' ? 'border-green-500 text-green-500' : 'border-yellow-500 text-yellow-500'}
                              >
                                {u.status?.toUpperCase()}
                              </Badge>
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm mb-3">
                              <div>
                                <span className="text-[#008F11]">Pairs: </span>
                                <span className="text-cyan-400">{u.active_pairs?.join(', ') || 'None'}</span>
                              </div>
                              <div>
                                <span className="text-[#008F11]">Buys: </span>
                                <span className="text-cyan-400">{u.total_buys}</span>
                              </div>
                              <div>
                                <span className="text-[#008F11]">Sells: </span>
                                <span className="text-orange-400">{u.total_sells}</span>
                              </div>
                              <div>
                                <span className="text-[#008F11]">Profit: </span>
                                <span className={u.total_profit >= 0 ? 'text-green-500' : 'text-red-500'}>
                                  ${u.total_profit?.toFixed(2)}
                                </span>
                              </div>
                              <div>
                                <span className="text-[#008F11]">Win Rate: </span>
                                <span className="text-yellow-400">{u.win_rate}%</span>
                              </div>
                            </div>

                            {/* Recent Trades */}
                            {u.recent_trades?.length > 0 && (
                              <div className="mt-3 border-t border-[#003B00] pt-3">
                                <div className="text-[#008F11] text-xs mb-2">RECENT TRADES:</div>
                                <div className="space-y-1 max-h-32 overflow-y-auto">
                                  {u.recent_trades.map((t, idx) => (
                                    <div key={idx} className="flex items-center gap-3 text-xs font-mono">
                                      <span className={t.type === 'BUY' ? 'text-cyan-400' : 'text-orange-400'}>
                                        {t.type}
                                      </span>
                                      <span className="text-[#00FF41]">{t.pair}</span>
                                      <span className="text-[#008F11]">${t.usd_value?.toFixed(2)}</span>
                                      {t.profit !== null && (
                                        <span className={t.profit >= 0 ? 'text-green-500' : 'text-red-500'}>
                                          P/L: ${t.profit?.toFixed(2)}
                                        </span>
                                      )}
                                      <span className="text-[#006600]">
                                        {new Date(t.timestamp).toLocaleString()}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Last Updated */}
                  <div className="text-[#006600] text-xs text-right">
                    Last updated: {liveData.timestamp ? new Date(liveData.timestamp).toLocaleString() : 'N/A'}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPanel;
